import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Label } from '@/components/ui/label'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { CaretRightIcon } from '@phosphor-icons/react'

/**
 * The form grammar every dialog shares (docs/DESIGN-dialog-grammar.md).
 *
 * Every control in a dialog is the same shape: a quiet filled field with no
 * border, so a form reads as one column of equal boxes rather than a mix of
 * outlined inputs, bordered editors and tinted pickers. Keyboard focus is the
 * app's shared outline; pointer focus only deepens the fill. Pass this to an
 * Input, a SelectTrigger, a combobox trigger Button, a DateInputPicker's
 * `triggerClassName`, a RichTextEditor's `className`, and so on.
 */
export const DIALOG_FIELD_CLASS =
  'h-9 rounded-md border-0 bg-muted/30 shadow-none hover:bg-muted/40 focus:border-0 focus:bg-muted/50 focus-within:bg-muted/50'

/** A multi-line field: the same fill without the fixed height. */
export const DIALOG_FIELD_AREA_CLASS =
  'min-h-[72px] rounded-md border-0 bg-muted/30 shadow-none hover:bg-muted/40 focus:border-0 focus:bg-muted/50 focus-within:bg-muted/50'

/** Validation copy under a field. */
export const DIALOG_FIELD_ERROR_CLASS = 'text-xs text-destructive'

/** Read-only or secondary copy under a field. */
export const DIALOG_FIELD_HINT_CLASS = 'text-xs text-muted-foreground'

/** The gap between label and control, and between stacked fields. */
export const DIALOG_FIELD_STACK_CLASS = 'space-y-1.5'
export const DIALOG_FORM_STACK_CLASS = 'space-y-4'

export interface DialogFieldProps {
  /** Plain sentence-case text. No icon, no colon. */
  label?: ReactNode
  htmlFor?: string
  required?: boolean
  error?: ReactNode
  hint?: ReactNode
  className?: string
  /** Something small to the right of the label, like a count or a link. */
  labelAction?: ReactNode
  children: ReactNode
}

/**
 * One labelled control. The label sits one step quieter than the control,
 * the control fills the width, and validation copy hangs under it.
 */
export function DialogField({
  label,
  htmlFor,
  required,
  error,
  hint,
  className,
  labelAction,
  children,
}: DialogFieldProps) {
  return (
    <div className={cn(DIALOG_FIELD_STACK_CLASS, 'min-w-0', className)}>
      {label !== undefined && label !== null && (
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor={htmlFor} required={required}>
            {label}
          </Label>
          {labelAction}
        </div>
      )}
      {children}
      {error ? (
        <p className={DIALOG_FIELD_ERROR_CLASS}>{error}</p>
      ) : hint ? (
        <p className={DIALOG_FIELD_HINT_CLASS}>{hint}</p>
      ) : null}
    </div>
  )
}

export interface DialogFieldRowProps {
  /** Grid template. Defaults to two equal columns. */
  columns?: string
  className?: string
  children: ReactNode
}

/** Two (or more) fields on one line. Collapses to one column on phones. */
export function DialogFieldRow({
  columns,
  className,
  children,
}: DialogFieldRowProps) {
  return (
    <div
      className={cn('grid gap-3', !columns && 'sm:grid-cols-2', className)}
      style={columns ? { gridTemplateColumns: columns } : undefined}
    >
      {children}
    </div>
  )
}

export interface DialogFoldProps {
  /** The verb phrase on the folded link, without the plus: "Add description". */
  label: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * When the field already carries a value the fold is meaningless, so the
   * caller passes `hasValue` and the content stays open regardless.
   */
  hasValue?: boolean
  disabled?: boolean
  className?: string
  children: ReactNode
}

/**
 * An optional field that starts as a one-line "+ Add …" link and unfolds in
 * place when asked for. Use it for the one heavy optional field in a form
 * (a description editor, an attachment list), not for every optional field.
 */
export function DialogFold({
  label,
  open,
  onOpenChange,
  hasValue = false,
  disabled = false,
  className,
  children,
}: DialogFoldProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const shouldFocus = useRef(false)
  useEffect(() => {
    if (!open || !shouldFocus.current) return
    shouldFocus.current = false
    const frame = requestAnimationFrame(() => {
      const input = contentRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), [contenteditable="true"]',
      )
      const target =
        input ??
        contentRef.current?.querySelector<HTMLElement>('button:not([disabled])')
      target?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [open])
  if (open || hasValue) {
    return <div ref={contentRef}>{children}</div>
  }
  return (
    <button
      type="button"
      onClick={() => {
        shouldFocus.current = true
        onOpenChange(true)
      }}
      disabled={disabled}
      className={cn(
        'rounded-md px-1 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50',
        FOCUS_RING,
        className,
      )}
    >
      + {label}
    </button>
  )
}

export interface DialogMoreOptionsProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Defaults to "More options". */
  label?: string
  /**
   * The current values of the folded fields, e.g. "Medium · Today · Private",
   * shown while folded so a default is never hidden from the user.
   */
  summary?: ReactNode
  disabled?: boolean
  className?: string
  children: ReactNode
}

/**
 * Everything a form does not need up front folds behind one row. The
 * trigger carries the current values so a folded default is still visible
 * without opening it.
 */
export function DialogMoreOptions({
  open,
  onOpenChange,
  label = 'More options',
  summary,
  disabled = false,
  className,
  children,
}: DialogMoreOptionsProps) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className={className}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'group flex w-full items-center gap-1.5 rounded-md py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50',
            FOCUS_RING,
          )}
        >
          <CaretRightIcon
            className="h-3 w-3 shrink-0 transition-transform duration-150 group-data-[state=open]:rotate-90"
            weight="bold"
          />
          <span className="font-medium">{label}</span>
          {!open && summary && (
            <span className="min-w-0 truncate text-muted-foreground/70">
              {summary}
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn(DIALOG_FORM_STACK_CLASS, 'pt-2')}>
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Joins the values a DialogMoreOptions summary shows, dropping empties.
 */
export function joinSummary(
  parts: Array<string | null | undefined | false>,
): string {
  return parts.filter(Boolean).join(' · ')
}
